import React, {useRef, useState} from 'react'
import Link from 'next/link'
import {useTranslation} from 'next-i18next'

import {Container, TwoColumns, Box, TemplateLoader, TemplateOpener} from '@miro-site/core'
import {
  CardGrid,
  TemplateCard,
  HighlightedCard,
  Main,
  CategoryHeader,
  TemplateCardProps,
  CategoryGroup,
} from '@miro-site/templates-design'

import ctaImage from '../../public/static/images/community-templates-creators.png'

import TemplatesSearch from './TemplatesSearch'
import {TemplateCardData} from '../data/mappers/toTemplateCard'
import {TemplateHighlightedCardData} from '../data/mappers/toHighlightedCard'

export interface MainPageCategorySectionProps {
  title: string
  totalCount: number
  templates: TemplateCardData[]
  renderLink?: (
    defaultLink: React.ReactElement,
    defaultLabel: string,
    categoryPath: string,
  ) => React.ReactNode
  categoryPath: string
}

export type MainPageProps = {
  title: string
  description: string
  categoryGroups: CategoryGroup[]
  highlightedTemplates?: TemplateHighlightedCardData[]
  categories?: MainPageCategorySectionProps[]
}

const MainPage = (props: MainPageProps): React.ReactElement => {
  const {title, description, categoryGroups, highlightedTemplates = [], categories = []} = props

  const headerRef = useRef<HTMLElement>(null)

  const [metaId, setMetaId] = useState(null)
  const [templateId, setTemplateId] = useState(null)

  const {t} = useTranslation('common')

  const getActionButtonProps = ({
    appSlug,
    newBoardMetadataId,
    accessConstraints,
  }: TemplateCardData): TemplateCardProps['actionButtonProps'] => {
    if (accessConstraints?.requiredPlans?.length > 0) {
      return {
        children: t('templates-actions-open-template'),
        onClick: e => {
          e.preventDefault()
          setMetaId(null)
          setTemplateId(appSlug)
        },
      }
    }

    return {
      onClick: e => {
        e.preventDefault()
        setTemplateId(null)
        setMetaId(newBoardMetadataId)
      },
    }
  }

  return (
    <>
      <main data-selected-meta-id={metaId} data-selected-appslug={templateId}>
        <Box $mt={30} $mb={80}>
          <TemplatesSearch categoryGroups={categoryGroups} stickyFixingPoint={headerRef} />
        </Box>
        <Container ref={headerRef}>
          <Main.Title>{title}</Main.Title>
          <Main.Description>{description}</Main.Description>
        </Container>
        {highlightedTemplates.length > 0 && (
          <CardGrid isHighlighted={true}>
            {highlightedTemplates.map((template, i) => (
              <HighlightedCard
                templateActions={{
                  PREVIEW: t('templates-actions-preview'),
                  USE_TEMPLATE: t('templates-actions-use-template'),
                  SHOW_MORE_INFO: t('templates-actions-more-info'),
                }}
                key={template.title}
                previewButton={defaultButton => (
                  <Link href={template.templatePagePath} passHref legacyBehavior>
                    {defaultButton}
                  </Link>
                )}
                actionButtonProps={getActionButtonProps(template)}
                position={i}
                {...template}
              />
            ))}
          </CardGrid>
        )}
        {categories.map((categorySection, i) => (
          <React.Fragment key={categorySection.title}>
            <CardGrid
              top={
                <Link href={categorySection.categoryPath} passHref legacyBehavior>
                  <CategoryHeader
                    data-testid="view-all-link"
                    viewAllLabel={t('view-all-link', {
                      templatesCount: categorySection.totalCount,
                    })}
                    title={categorySection.title}
                  />
                </Link>
              }
            >
              {categorySection.templates.map(template => (
                <TemplateCard
                  templateActions={{
                    PREVIEW: t('templates-actions-preview'),
                    USE_TEMPLATE: t('templates-actions-use-template'),
                    SHOW_MORE_INFO: t('templates-actions-more-info'),
                  }}
                  key={template.title}
                  previewButton={defaultButton => (
                    <Link href={template.templatePagePath} passHref legacyBehavior>
                      {defaultButton}
                    </Link>
                  )}
                  actionButtonProps={getActionButtonProps(template)}
                  {...template}
                />
              ))}
            </CardGrid>

            {i === 0 && (
              <TwoColumns
                headline={t('miroverse-cta-headline')}
                content={t('miroverse-cta-description')}
                button={{
                  href: '/miroverse',
                  children: t('miroverse-cta-button'),
                  $buttonType: 'primary',
                  $size: 'medium',
                  $withArrow: true,
                }}
                image={{
                  width: 550,
                  height: 550,
                  name: t('miroverse-cta-headline'),
                  original: {
                    png: ctaImage.src,
                  },
                }}
              />
            )}
          </React.Fragment>
        ))}
      </main>
      <TemplateLoader metadataId={metaId} setMetadataId={setMetaId} />
      <TemplateOpener templateId={templateId} setTemplateId={setTemplateId} />
    </>
  )
}

export default MainPage
